import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, createProject } from '../firebase';
import { stateToPlan } from './capturePlan';

// Centralized capture-plan persistence — one place both the in-project card
// (CapturePlanCard) and the public calculator (SaveCalculationDialog) call, so
// the write shape (`capturePlan: { ...plan, savedAt }`) never drifts between them.

// Write the plan built from `state` onto an existing project doc. Returns the
// stored payload so callers can summarize/track it without rebuilding the plan.
// Pass `projectName` to also rename the project in the same write — the
// calculator's new-project retry uses it so a reused (orphaned) project ends up
// with the name shown in the success message, even if the user edited it
// between attempts.
export async function saveCapturePlanToProject(projectId, state, { projectName } = {}) {
  const payload = { ...stateToPlan(state), savedAt: serverTimestamp() };
  const update = { capturePlan: payload };
  if (typeof projectName === 'string' && projectName.trim()) {
    update.projectName = projectName.trim();
  }
  await updateDoc(doc(db, 'projects', projectId), update);
  return payload;
}

// Create a brand-new project then persist the plan into it — the calculator's
// signed-in "New project" path, kept here so account-less users convert in one step.
export async function createProjectWithPlan(projectName, state) {
  const { data } = await createProject({ projectName });
  const projectId = data.newProjectId;
  try {
    const payload = await saveCapturePlanToProject(projectId, state);
    return { projectId, payload };
  } catch (e) {
    // The project was created but the plan write failed. Tag the orphan's id
    // onto the error so the caller can retry the write against it instead of
    // orphaning a second empty project on the next attempt.
    if (e && typeof e === 'object') e.projectId = projectId;
    throw e;
  }
}
